"use client";

import { useState, useRef, useEffect, type ReactElement } from "react";
import {
  useModelStore,
  syncModelPreference,
  DEFAULT_MODEL,
} from "@/stores/modelStore";
import { useRuntimeStore } from "@/stores/runtimeStore";
import { useModeStore } from "@/stores/modeStore";
import {
  useEnginePreferenceStore,
  syncEnginePreference,
} from "@/stores/enginePreferenceStore";
import { requestTaskSync, useTaskStore } from "@/stores/taskStore";
import { useHistoryStore } from "@/stores/historyStore";
import {
  modelGroups,
  modelForEngine,
  brandForModelId,
  shortLabelForModel,
  type Brand,
} from "./model-catalog";
import { cn } from "@/lib/utils";
import { posthog } from "@/lib/posthog";
import { ANALYTICS_EVENTS } from "@bash/shared";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { ProviderIconTile, BareBrandMark } from "./provider-icons";
import { CheckIcon } from "@/components/icons/CheckIcon";
import { LockCompactIcon } from "@/components/icons/LockCompactIcon";
import { ChevronToggleIcon } from "@/components/icons/ChevronToggleIcon";

/**
 * The composer's model control — the panes sheet (founder 08-27, the Bash
 * home's final shape, since made the only one):
 *
 *   closed  →  one pill: [provider icon][short model name]   (DeepSeek ▸ Pro)
 *   open    →  one sheet: the maker houses down the left as coin cards, the
 *              highlighted house's models on the right over its own mark
 *              drawn large and faint. Hovering a house switches the right
 *              pane in place — the sheet itself is never re-created, so
 *              nothing snaps. Picking a model commits and closes.
 *
 * Picking commits (runtime='native', model) together. Access/permissions stay
 * a SEPARATE control (TaskAccessLevelPicker). Interactive only on the new-task
 * surface; once a task is active the pill is read-only (harness+model locked
 * at creation and enforced server-side). Row labels are clean family names
 * (no raw ids, no "Claude" in system prose); brand marks are the accepted
 * carve-out.
 *
 * Positioning: the sheet anchors to the trigger's right edge and clamps to
 * the viewport with a 12px gutter, shifting left only as far as the gutter
 * needs.
 *
 * On Claude Code, the person's own plan (engines T2b, founder 09-30: "show only
 * claude models here like sonnet opus"), the sheet lists only the models that
 * engine runs (PROVIDERS_BY_HARNESS.claude_code: the Anthropic house) in one
 * column, with no maker column. The pill shows the person's model when it is a
 * Claude one, else the engine's default for the mode, and the pick rides the
 * send like any pick (resolveComposerModel).
 */

export function ModelSelector({
  compact = false,
  forNewTask = false,
  placement = "up",
}: {
  compact?: boolean;
  /**
   * The surface ALWAYS spawns a fresh task (home dock) — the pill is the
   * user's pick, never the lingering selected task's lock. Without this, the
   * last-viewed task's locked model wore its padlock on home (founder 07-22:
   * "on the main screen the model selector is locked") and read as intent for
   * the next send, which it never was.
   */
  forNewTask?: boolean;
  /** Which way the sheet opens. The bottom dock opens UP (default); a composer
   *  worn at the top of the page (the Bash home) opens DOWN. */
  placement?: "up" | "down";
} = {}): ReactElement {
  const pickedModel = useModelStore((s) => s.model);
  const setModel = useModelStore((s) => s.setModel);
  const setRuntime = useRuntimeStore((s) => s.setRuntime);

  const selectedTaskId = useTaskStore((s) => s.selectedTaskId);
  const pendingNewTaskId = useTaskStore((s) => s.pendingNewTaskId);
  const taskModelLive = useTaskStore((s) =>
    s.selectedTaskId ? s.tasks.get(s.selectedTaskId)?.model : undefined
  );
  const taskModelFromHistory = useHistoryStore((s) =>
    selectedTaskId
      ? s.taskHistory.find((t) => t.id === selectedTaskId)?.model
      : undefined
  );
  const taskModel = taskModelLive ?? taskModelFromHistory;

  const locked =
    !forNewTask && !!selectedTaskId && selectedTaskId !== pendingNewTaskId;
  const pending = locked && taskModel === undefined;

  useEffect(() => {
    if (locked && taskModel === undefined && selectedTaskId) {
      requestTaskSync(selectedTaskId);
    }
  }, [locked, taskModel, selectedTaskId]);

  useEffect(() => {
    void syncModelPreference();
    void syncEnginePreference();
  }, []);
  const engine = useEnginePreferenceStore((s) => s.engine) ?? "native";
  const mode = useModeStore((s) => s.mode);
  // Vestra runs every maker's models; another engine runs one maker's, so the
  // sheet has no maker column to choose from there.
  const makerColumn = engine === "native";

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const activeModel = locked
    ? taskModel
    : mounted
      ? modelForEngine(pickedModel, engine, mode)
      : DEFAULT_MODEL;
  const activeBrand = brandForModelId(activeModel);
  const activeShort = shortLabelForModel(activeModel);

  const [open, setOpen] = useState(false);
  const [houseBrand, setHouseBrand] = useState<Brand | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  /** Radix focuses the Content itself (then default tab order) on open unless
   *  told otherwise — which raced ahead of a plain effect here and won, since
   *  Radix schedules its auto-focus after mount while an effect in the parent
   *  fires first. Doing the focus placement IN the callback that would set
   *  Radix's default, with `preventDefault`, is what actually wins the race. */
  function onMenuOpenAutoFocus(e: Event) {
    e.preventDefault();
    (
      menuRef.current?.querySelector<HTMLButtonElement>(
        "[data-deck-house][data-holds-active]"
      ) ??
      menuRef.current?.querySelector<HTMLButtonElement>("[data-deck-house]") ??
      // No maker column: the keyboard lands on the model the pill shows.
      menuRef.current?.querySelector<HTMLButtonElement>(
        '[data-model-row][aria-selected="true"]'
      ) ??
      menuRef.current?.querySelector<HTMLButtonElement>("[data-model-row]")
    )?.focus();
  }

  const groups = modelGroups(engine);

  /** The model rows the keyboard may walk. The sheet keeps every house's list
   *  mounted (so a hover never resizes it) and marks the hidden ones `inert`;
   *  their rows are not live. */
  const liveRows = () =>
    Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>(
        "[data-model-row]"
      ) ?? []
    ).filter((r) => !r.closest("[inert]"));

  /** Houses: Up/Down walk the column with wrap, Right enters the open house's
   *  models. Models: Up/Down walk the rows, Left returns to the houses
   *  column. Enter/Space are native. */
  function onMenuKeyDown(e: React.KeyboardEvent) {
    if (
      ![
        "ArrowDown",
        "ArrowUp",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
      ].includes(e.key)
    )
      return;
    const el = document.activeElement as HTMLButtonElement | null;
    const inHouses = !!el?.hasAttribute("data-deck-house");
    if (!inHouses && e.key === "ArrowLeft") {
      e.preventDefault();
      menuRef.current
        ?.querySelector<HTMLButtonElement>(
          '[data-deck-house][aria-selected="true"]'
        )
        ?.focus();
      return;
    }
    if (inHouses && e.key === "ArrowRight") {
      e.preventDefault();
      const rows = liveRows();
      (
        rows.find((r) => r.getAttribute("aria-selected") === "true") ?? rows[0]
      )?.focus();
      return;
    }
    const column = inHouses
      ? Array.from(
          menuRef.current?.querySelectorAll<HTMLButtonElement>(
            "[data-deck-house]"
          ) ?? []
        )
      : liveRows();
    if (column.length === 0) return;
    e.preventDefault();
    const forward = e.key === "ArrowDown" || e.key === "ArrowRight";
    const i = column.indexOf(el as HTMLButtonElement);
    const next =
      e.key === "Home"
        ? 0
        : e.key === "End"
          ? column.length - 1
          : forward
            ? i < 0
              ? 0
              : (i + 1) % column.length
            : i < 0
              ? column.length - 1
              : (i - 1 + column.length) % column.length;
    column[next]?.focus();
  }

  /** The house whose models the right pane shows: the hovered one, or with no
   *  maker column, every house the engine runs (one). */
  const isOpenHouse = (brand: Brand) => !makerColumn || houseBrand === brand;

  function openDeck() {
    setHouseBrand(activeBrand);
    setOpen(true);
  }

  /** Commit a model on the native harness and close. */
  function pickModel(modelId: string) {
    // The CHOICE as an event — the model already rides turn events as a
    // property, but that cannot measure picker usage or switching.
    posthog.capture(ANALYTICS_EVENTS.MODEL_SELECTED, { model: modelId });
    setRuntime("native");
    setModel(modelId);
    setOpen(false);
  }

  const tooltipContent = locked
    ? pending
      ? "Model loading, fixed for this task"
      : `Model: ${activeShort}, fixed for this task`
    : "Choose model";
  // Compact hides the name from the trigger, so the hover carries it.
  const hoverContent =
    !locked && compact ? `Model: ${activeShort}` : tooltipContent;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative" onKeyDown={open ? onMenuKeyDown : undefined}>
        {/* Trigger pill — provider icon + short model name (the brand's mark carries the prefix). */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                tabIndex={locked ? 0 : undefined}
                className={`${locked ? "cursor-not-allowed" : ""}`}
              >
                <PopoverTrigger asChild>
                  <Button
                    ref={triggerRef}
                    variant="icon"
                    size="xxs-icon"
                    type="button"
                    onClick={() => {
                      if (locked) return;
                      if (open) setOpen(false);
                      else openDeck();
                    }}
                    disabled={locked}
                    aria-label={tooltipContent}
                    className="gap-2 disabled:opacity-90"
                  >
                    {pending ? (
                      <span className="text-fg-faint" aria-hidden>
                        …
                      </span>
                    ) : (
                      <>
                        <ProviderIconTile
                          provider={activeBrand}
                          tileSize={18}
                        />
                        {/* Compact (the Computer panel is squeezing the composer) OR mobile:
                  the provider mark IS the pill — name + chevron come back at md+.
                  The aria-label/title keep the full meaning. */}
                        {!compact && (
                          <span className="sr-only md:not-sr-only">
                            {activeShort}
                          </span>
                        )}
                      </>
                    )}
                    {locked ? (
                      <LockCompactIcon className="text-fg-faint" aria-hidden />
                    ) : (
                      !compact && (
                        <ChevronToggleIcon
                          open={open}
                          className="hidden md:inline text-fg-faint"
                        />
                      )
                    )}
                  </Button>
                </PopoverTrigger>
              </div>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              sideOffset={6}
              className={cn(open && "hidden")}
            >
              {hoverContent}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* The sheet — never opens while locked. */}
        <PopoverContent
          side={placement === "down" ? "bottom" : "top"}
          align="start"
          collisionPadding={12}
          sideOffset={10}
          ref={menuRef}
          onOpenAutoFocus={onMenuOpenAutoFocus}
          className={cn(
            "p-0 z-[80] deck-panes flex flex-col-reverse items-start gap-1 [&_.deck-house]:[align-self:flex-start] grid overflow-visible",
            makerColumn
              ? "grid-cols-[72px_minmax(0,_1fr)] xs:grid-cols-[196px_minmax(0,_1fr)] w-[min(568px,_calc(100vw_-_24px))]"
              : "grid-cols-1 w-[min(372px,_calc(100vw_-_24px))]"
          )}
        >
          {makerColumn && (
            <div
              // self-stretch: the sheet's `items-start` would end this column
              // (its tint and rule) at the last house, above the sheet's bottom
              // edge, now that the sheet is as tall as the tallest list.
              className="self-stretch grid [align-content:start] gap-2 p-2.5 border-r border-border-subtle bg-bg-subtle/60 rounded-[16px_0_0_16px]"
              role="tablist"
              aria-label="Model makers"
            >
              {groups.map((g) => {
                const on = houseBrand === g.brand;
                return (
                  <button
                    type="button"
                    key={g.brand}
                    role="tab"
                    data-deck-house
                    data-holds-active={
                      g.rows.some((r) => r.id === activeModel) || undefined
                    }
                    aria-selected={on}
                    aria-label={`House: ${g.header}`}
                    onMouseEnter={() => setHouseBrand(g.brand)}
                    onClick={() => setHouseBrand(g.brand)}
                    className={cn(
                      "relative flex items-center gap-2.5 w-full py-2 px-2.5 border-none rounded-12 bg-none cursor-pointer [font-family:inherit] text-left transition-[background,box-shadow] duration-150 ease-out hover:bg-[color-mix(in_srgb,var(--bg-elevated)_70%,transparent)] focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_var(--gold-molten-mid)]",
                      on
                        ? "bg-bg-elevated shadow-model-selector-3 hover:bg-bg-elevated hover:shadow-model-selector-3"
                        : ""
                    )}
                  >
                    <span
                      className="deck-panes-coin inline-grid [place-items:center] flex-none w-7.5 h-7.5 rounded-full bg-bg-base border-[1.5px] border-solid border-bg-elevated shadow-account-profile dark:bg-[#e8e4da] dark:border-border-strong dark:[&_svg]:text-[#0b0b0c]"
                      aria-hidden
                    >
                      <BareBrandMark
                        brand={g.brand}
                        size={g.brand === "xai" ? 18 : 15}
                      />
                    </span>
                    {/* The house's name alone; the model count under it went (founder 09-01). */}
                    <span className="max-xs:hidden min-w-0 text-left">
                      <span className="block text-3.5 font-medium tracking-[-0.005em] text-fg-default">
                        {g.header}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <div
            className={cn(
              "relative overflow-hidden grid [align-content:start]",
              makerColumn ? "rounded-[0_16px_16px_0]" : "rounded-[16px]"
            )}
          >
            {/* Every house's mark lives in the pane and only its opacity moves,
                so a hover crossfades instead of re-creating an image. */}
            {groups.map((g) => (
              <span
                key={g.brand}
                data-deck-watermark={g.brand}
                data-on={isOpenHouse(g.brand) || undefined}
                className={cn(
                  "absolute -right-8.5 -bottom-9.5 w-59 h-59 opacity-0 pointer-events-none text-fg-default transition-opacity duration-280 ease-in-out [&_svg]:w-full [&_svg]:h-full [&_svg]:text-inherit",
                  g.brand === "xai"
                    ? "w-110 h-auto -right-50 -bottom-35 [&_svg]:h-auto"
                    : "",
                  isOpenHouse(g.brand) ? "opacity-6" : ""
                )}
                aria-hidden
              >
                <BareBrandMark
                  brand={g.brand}
                  size={g.brand === "xai" ? 330 : 236}
                  ink
                />
              </span>
            ))}
            {/* Every house's list stays mounted, stacked in ONE grid cell, so
                the sheet is as tall as the tallest list whichever house is
                open. A bottom-pinned sheet that resized on hover grew upward
                and slid the houses under the pointer: SpaceX → Anthropic
                flickered (founder 09-01). Hidden lists are inert and
                transparent; a hover crossfades, nothing remounts. */}
            <div className="relative grid grid-cols-1 pt-3.5 px-3 pb-2">
              {/* No header over the models: the highlighted house on the left already names them (founder 09-01). */}
              {groups.map((g) => {
                const on = isOpenHouse(g.brand);
                return (
                  <div
                    key={g.brand}
                    role="listbox"
                    aria-label={`${g.header} models`}
                    data-deck-panel={g.brand}
                    aria-hidden={!on || undefined}
                    inert={!on}
                    className={cn(
                      "col-start-1 row-start-1 grid gap-2 [align-content:start] transition-opacity duration-150 ease-out",
                      on ? "opacity-100" : "opacity-0 pointer-events-none"
                    )}
                  >
                    {g.rows.map((row) => {
                      const isActive = activeModel === row.id;
                      if (row.comingSoon) {
                        return (
                          <div
                            key={row.id}
                            aria-disabled="true"
                            className="flex items-center gap-3 w-full min-w-0 py-2.5 px-3 border-none rounded-12 bg-none cursor-pointer [font-family:inherit] text-left transition-colors duration-[120ms] hover:bg-bg-muted focus-visible:bg-bg-muted focus-visible:outline-none [&_.deck-check]:w-3.5 [&_.deck-check]:h-3.5 opacity-60 cursor-default"
                          >
                            <span className="min-w-0 text-left">
                              <span className="text-[13px] font-normal text-fg-default block truncate">
                                {row.label}{" "}
                                <span className="text-2.5 font-semibold py-px px-1.5 rounded-4 uppercase tracking-[0.06em] border border-amber text-amber">
                                  Soon
                                </span>
                              </span>
                              <span className="text-3 leading-[1.4] text-fg-subtle">
                                {row.comingSoon}
                              </span>
                            </span>
                          </div>
                        );
                      }
                      return (
                        <button
                          key={row.id}
                          type="button"
                          role="option"
                          aria-selected={isActive}
                          data-model-row
                          onClick={() => pickModel(row.id)}
                          className={cn(
                            "deck-panes-row flex items-center gap-3 w-full min-w-0 py-2 px-3 rounded-12 text-left",
                            // The picked model is a transparent card with a warm
                            // ring, so the house's mark behind it stays in view,
                            // and a gold check at its right (founder 09-01).
                            isActive
                              ? "shadow-[0_0_0_1px_color-mix(in_srgb,var(--gold-molten-mid)_38%,var(--border-subtle))]"
                              : "hover:shadow-[0_0_0_1px_color-mix(in_srgb,_var(--fg-subtle)_12%,_transparent)]"
                          )}
                        >
                          <span className="flex-1 min-w-0 text-left">
                            <span className="text-[13px] font-normal text-fg-default block truncate">
                              {row.label}
                            </span>
                            <span className="text-3 leading-[1.4] text-fg-subtle block truncate">
                              {row.blurb}
                            </span>
                          </span>
                          {isActive && (
                            <CheckIcon
                              width={13}
                              height={13}
                              strokeWidth="3"
                              className="text-[var(--gold-molten-mid,_var(--color-amber))] shrink-0"
                              aria-hidden
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
              <div className="flex justify-between mt-4 pt-2.5 px-2 pb-1 border-t border-border-subtle font-mono text-2.5 tracking-[0.02em] text-fg-faint">
                <span>Applies to your next task</span>
                <span>esc</span>
              </div>
            </div>
          </div>
        </PopoverContent>
      </div>
    </Popover>
  );
}
